import { useData } from "../../runtime/provider";
import PlaylistSongSuggestionRow from "@components/Collection/playlist/PlaylistSongSuggestionRow";
import LBox from "@components/LBox";
import LFlatList from "@components/LFlatList";
import LIcon from "@components/LIcon";
import LInput from "@components/LInput";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { useKeyboardHeight } from "@hooks/useKeyboardHeight";
import { useSearch as useSearchAll } from "@hooks/useSearchAll";
import { useAddSong } from "@hooks/trpc/usePlaylists";
import { useRecommendedSongs } from "@hooks/trpc/useRecommendedSongs";
import type { Song } from "@libs/wire";
import { theme } from "@theme";
import AppModalSheet from "@utils/appModal/components/AppModalSheet";

import { forwardRef, useMemo, useState } from "react";
import { ActivityIndicator, Dimensions } from "react-native";
import type { SheetAdapterRef } from "react-native-bottom-sheet-stack";
import { BottomSheetTopBar } from "@utils/swmModal/components/BottomSheetPrimitives";
import { toast } from "sonner-native";

const SHEET_HEIGHT = Math.min(Dimensions.get("window").height * 0.84, 740);

type AddSongsToPlaylistModalProps = {
  existingSongIds: string[];
  playlistId: string;
};

const AddSongsToPlaylistModal = ({
  existingSongIds,
  playlistId,
}: AddSongsToPlaylistModalProps) => {
  const session = useData();
  const { data: recommendations, isLoading: areRecommendationsLoading } =
    useRecommendedSongs();
  const {
    isSearching,
    recordSearchSelection,
    searchResults,
    searchTerm,
    setSearchTermInput,
  } = useSearchAll(
    session.utils.catalog.searchAnything,
    "mobile_add_to_playlist",
  );
  const { addSong } = useAddSong();
  const [addedSongIds, setAddedSongIds] = useState<string[]>([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const keyboardHeight = useKeyboardHeight();
  const isSearchMode = isSearchFocused || searchTerm.length > 0;
  const contentHeight = Math.max(SHEET_HEIGHT - keyboardHeight, 240);

  const songs = useMemo(() => {
    const excludedIds = new Set([...existingSongIds, ...addedSongIds]);
    const source: Song[] = isSearchMode
      ? searchResults.filter((result) => result.type === "song")
      : recommendations ?? [];

    return source.filter(
      (song) => !excludedIds.has(song.id) && song.status !== "external",
    );
  }, [
    addedSongIds,
    existingSongIds,
    isSearchMode,
    recommendations,
    searchResults,
  ]);

  const handleAddSong = (song: Song) => {
    if (searchTerm.length > 0) {
      recordSearchSelection({ id: song.id, type: "song" }, songs.indexOf(song));
    }
    addSong([playlistId], song);
    setAddedSongIds((current) => [...current, song.id]);
    toast.success("Song added to playlist", { duration: 1500 });
  };

  const isLoading = isSearchMode
    ? searchTerm.length > 0 && isSearching
    : areRecommendationsLoading;

  return (
    <LBox height={contentHeight} pb="l1">
      <BottomSheetTopBar title="Add songs" />
      <LBox flex={1} px="s4" pt="s2">
        <LText color="black" mb="s2" variant="bodyLBold">
          {isSearchMode ? "Search results" : "Recommended for you"}
        </LText>
        <LFlatList
          data={songs}
          disableAutomaticPadding
          keyExtractor={(song) => song.id}
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
          renderItem={({ item }) => (
            <PlaylistSongSuggestionRow
              onAdd={handleAddSong}
              song={item}
              surface="light"
            />
          )}
          showsVerticalScrollIndicator={false}
          style={{ flex: 1 }}
          ListEmptyComponent={
            isLoading ? (
              <LBox alignItems="center" py="m1">
                <ActivityIndicator />
              </LBox>
            ) : (
              <LText color="blackTranslucent70" py="m1">
                {searchTerm
                  ? "No songs found. Try another search."
                  : isSearchMode
                    ? "Start typing to find songs."
                    : "No more recommendations right now."}
              </LText>
            )
          }
        />
        <LBox pt="s2">
          <LBox justifyContent="center">
            <LInput
              autoCapitalize="none"
              autoCorrect={false}
              onBlur={() => setIsSearchFocused(false)}
              onChangeText={(value) => {
                setSearchTermInput(value).catch(console.error);
              }}
              onFocus={() => setIsSearchFocused(true)}
              placeholder="What would you like to add?"
              placeholderTextColor={theme.colors.blackTranslucent50}
              returnKeyType="search"
              style={{
                backgroundColor: theme.colors.white,
                borderColor: theme.colors.blackTranslucent10,
                color: theme.colors.black,
                paddingLeft: 46,
                paddingRight: searchTerm ? 46 : theme.spacing.s3,
              }}
              testID="add-songs-search"
              value={searchTerm}
              variant="primary"
            />
            <LIcon
              colors={{ color: "greyText" }}
              left={theme.spacing.s3}
              name="search"
              position="absolute"
              size={24}
            />
            {searchTerm ? (
              <LPressable
                onPress={() => {
                  setSearchTermInput("").catch(console.error);
                }}
                sentryEventName="add_songs_clear_search"
                style={{ position: "absolute", right: theme.spacing.s3 }}
              >
                <LIcon name="circle_cross" size={24} />
              </LPressable>
            ) : null}
          </LBox>
        </LBox>
      </LBox>
    </LBox>
  );
};

const AddSongsToPlaylistModalSWMSheet = forwardRef<
  SheetAdapterRef,
  AddSongsToPlaylistModalProps
>(function AddSongsToPlaylistModalSWMSheet(props, ref) {
  return (
    <AppModalSheet detents={[0, SHEET_HEIGHT]} ref={ref} showHandle={false}>
      <AddSongsToPlaylistModal {...props} />
    </AppModalSheet>
  );
});

export default AddSongsToPlaylistModalSWMSheet;
